import { BadgeCheck } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { type SheetProps, View, XStack, YStack } from "tamagui";
import { ActivityEventUI } from "../../activities/ActivityEventUI";
import type ActivityModel from "../../activities/ActivityModel";
import { useApi } from "../../core/apis";
import { getDayJs } from "../../core/dayJs";
import { useStores } from "../../core/stores";
import type BaseModuleModel from "../../modules/core/BaseModuleModel";
import type {
  ModuleModelData,
  Snapshot,
} from "../../modules/core/BaseModuleModel";
import { usePlanNavigation } from "../../plans/usePlanNavigation";
import { CenterContent } from "../CenterContent";
import { Group } from "../Group";
import { Label } from "../Label";
import { Sheet } from "../Sheet";
import { Subtext } from "../Subtext";
import { Title } from "../Title";

export const ActivitySheet = observer(
  ({
    planUid,
    userUid,
    open,
    onOpenChange,
    ...props
  }: { planUid: string; userUid: string } & SheetProps) => {
    const { usersStore, plansStore } = useStores();
    const { activitiesApi } = useApi();
    const navigation = usePlanNavigation();

    const plan = plansStore.byId.get(planUid);
    const user = usersStore.byId.get(userUid);

    const [events, setEvents] = React.useState<
      {
        module: BaseModuleModel<Snapshot, ModuleModelData<Snapshot>>;
        event: ActivityModel;
      }[]
    >([]);

    const loadActivity = React.useCallback(async () => {
      if (user && plan) {
        const modules = plan.modulesWithNewActivity;
        const e = await activitiesApi.getEventsForModulesByTimestamp(
          modules,
          user,
        );
        setEvents(e);
      }
    }, [user, plan, activitiesApi]);

    const markAllAsRead = React.useCallback(async () => {
      if (plan && user) {
        // set new visited date for all modules with activity
        const modules = plan.modulesWithNewActivity;
        await Promise.all([
          modules.map((m) => {
            return user.updateVisitedTimestamp(m.uid);
          }),
        ]);
        setEvents([]);
      }
    }, [plan, user]);

    React.useEffect(() => {
      if (open) {
        loadActivity();
      }
    }, [loadActivity, open]);

    if (!open || !plan) {
      return null;
    }

    let lastDayTitle = "";
    return (
      <Sheet
        sheetId="notifications"
        open={open}
        onOpenChange={onOpenChange}
        {...props}
      >
        <YStack gap="$space.large">
          <XStack justifyContent="space-between" flex={1} alignItems="center">
            <Title>Notifications</Title>
            {events.length > 0 && (
              <Subtext
                onPress={markAllAsRead}
                cursor="pointer"
                textDecorationLine="underline"
              >
                Mark all as read
              </Subtext>
            )}
          </XStack>
          {events.length === 0 && (
            <CenterContent
              icon={BadgeCheck}
              title={"Nothing new"}
              subTitle={"Great! You are all up to speed."}
            />
          )}
          <Group>
            {events.map((eventData) => {
              const dayTitle = getDayJs(eventData.event.data.timestamp);
              let showDayTitle = "";
              if (dayTitle !== lastDayTitle) {
                showDayTitle = lastDayTitle = dayTitle;
              }
              return (
                <View key={eventData.event.uid}>
                  {!!showDayTitle && (
                    <View marginBottom="$medium">
                      <Label>{dayTitle}</Label>
                    </View>
                  )}

                  <YStack
                    cursor="pointer"
                    onPress={() => {
                      navigation.navigate("Module", {
                        planUid: planUid,
                        moduleUid: eventData.event.data.moduleUid,
                      });
                      onOpenChange?.(false);
                    }}
                    gap="$medium"
                  >
                    <ActivityEventUI
                      disableInteractions
                      event={eventData.event}
                      prefixInfoRowItems={[
                        {
                          text: eventData.module.data.title ?? "",
                        },
                      ]}
                    />
                  </YStack>
                </View>
              );
            })}
          </Group>
        </YStack>
      </Sheet>
    );
  },
);
